import * as React from 'react';
import { t } from '@reos/i18n';
import { Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { requestPasswordReset } from '../lib/api.js';

const requestSchema = z.object({
  email: z.string().email('Zadejte platný e-mail'),
});

type RequestForm = z.infer<typeof requestSchema>;

const CONFIRM_PATH = '/reset-password/confirm';

export function ResetPasswordRequestPage(): React.JSX.Element {
  const [submitted, setSubmitted] = React.useState(false);

  const form = useForm<RequestForm>({
    resolver: zodResolver(requestSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = form.handleSubmit(async (data) => {
    const redirectTo = `${window.location.origin}${CONFIRM_PATH}`;
    await requestPasswordReset(data.email, redirectTo);
    setSubmitted(true);
  });

  return (
    <div className="max-w-md mx-auto mt-12">
      <h1 className="text-2xl font-bold mb-2">{t('webAdmin.resetPassword.title', { locale: 'cs' })}</h1>
      {submitted ? (
        <p role="status" data-testid="reset-request-sent" className="text-sm text-neutral-700">
          {t('webAdmin.resetPassword.sent', { locale: 'cs' })}
        </p>
      ) : (
        <>
          <p className="text-sm text-neutral-600 mb-6">
            {t('webAdmin.resetPassword.hint', { locale: 'cs' })}
          </p>
          <form onSubmit={onSubmit} noValidate className="space-y-4" aria-label={t('webAdmin.resetPassword.formAriaLabel', { locale: 'cs' })}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1">{t('webAdmin.resetPassword.email', { locale: 'cs' })}</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                aria-describedby={form.formState.errors.email ? 'email-error' : undefined}
                {...form.register('email')}
                className="w-full border rounded px-3 py-2"
                data-testid="reset-request-email"
              />
              {form.formState.errors.email ? (
                <p id="email-error" role="alert" className="text-sm text-red-600 mt-1">
                  {form.formState.errors.email.message}
                </p>
              ) : null}
            </div>
            <button
              type="submit"
              disabled={form.formState.isSubmitting}
              className="w-full bg-neutral-900 text-rh-on-accent rounded px-4 py-2 hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2"
              data-testid="reset-request-submit"
            >
              {t('webAdmin.resetPassword.submit', { locale: 'cs' })}
            </button>
          </form>
        </>
      )}
      <p className="text-sm text-neutral-600 mt-6">
        <Link to="/login" data-testid="reset-request-back-to-login" className="underline">
          {t('webAdmin.resetPassword.backToLogin', { locale: 'cs' })}
        </Link>
      </p>
    </div>
  );
}
